import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { UnmatchedRide } from '../../../../types/simulation';
import { buildUnmatchedRideGoogleMapsDrivingUrl } from './utils';

import { Icon } from '@liftango/ui-icons';
/** Solid light surface + text colors for MUI Tooltip (avoids theme tokens that read poorly on dark tooltip chrome). */
export const UNMATCHED_RIDE_TOOLTIP_THEME = {
  surface: '#f8fafc',
  heading: '#0f172a',
  label: '#334155',
  value: '#0f172a',
  divider: 'rgba(15, 23, 42, 0.12)',
  /** Alternating label/value rows inside light tooltips */
  rowStripeEven: 'rgba(15, 23, 42, 0.04)',
  rowStripeOdd: 'rgba(15, 23, 42, 0.085)',
} as const;

/** Max height for Operations timeline unmatched-ride tooltips (viewport-relative, one scroll region). */
export const UNMATCHED_RIDE_TOOLTIP_MAX_HEIGHT = 'min(48vh, 340px)';

/** Popper styles for unmatched-ride Tooltips: light panel, dark text, readable shadow. */
export const unmatchedRideTooltipPopperSx = {
  '& .MuiTooltip-tooltip': {
    backgroundColor: UNMATCHED_RIDE_TOOLTIP_THEME.surface,
    color: UNMATCHED_RIDE_TOOLTIP_THEME.value,
    boxShadow: '0 8px 28px rgba(15, 23, 42, 0.18)',
    border: `1px solid ${UNMATCHED_RIDE_TOOLTIP_THEME.divider}`,
    maxWidth: 420,
    maxHeight: UNMATCHED_RIDE_TOOLTIP_MAX_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    // Extra right padding so header controls (info, maps) sit left of the scrollbar
    padding: '12px 20px 12px 14px',
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    fontFamily: '"Roboto", system-ui, -apple-system, "Segoe UI", sans-serif',
    WebkitOverflowScrolling: 'touch',
  },
  '& .MuiTooltip-arrow': {
    color: UNMATCHED_RIDE_TOOLTIP_THEME.surface,
  },
} as const;

function formatCoord(n: unknown): string | null {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  return n.toFixed(6);
}

/** Single-line "lat, lon" for pickup or dropoff (em dash when one side missing). */
function formatLatLonLine(
  ride: UnmatchedRide,
  latField: 'from_latitude' | 'to_latitude',
  lonField: 'from_longitude' | 'to_longitude'
): string | null {
  const lat = formatCoord(ride[latField]);
  const lon = formatCoord(ride[lonField]);
  if (lat == null && lon == null) return null;
  return `${lat ?? '—'}, ${lon ?? '—'}`;
}

function formatPassengers(passengers: unknown): string | null {
  if (!Array.isArray(passengers) || passengers.length === 0) return null;
  const parts: string[] = [];
  for (const p of passengers) {
    if (p != null && typeof p === 'object') {
      const o = p as Record<string, unknown>;
      const n = typeof o.number_of_passengers === 'number' ? o.number_of_passengers : 1;
      const t = typeof o.type === 'string' ? o.type : 'standard';
      parts.push(`${n}× ${t}`);
    }
  }
  return parts.length ? parts.join(', ') : null;
}

function str(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v.trim() === '' ? null : v;
  return String(v);
}

export type UnmatchedRideColumn = {
  id: string;
  label: string;
  get: (ride: UnmatchedRide) => string | null;
  monospace?: boolean;
};

/**
 * Column order for Unmatched table and Operations tooltips.
 * Ride / batch UUIDs live in {@link UNMATCHED_RIDE_IDENTIFIER_COLUMN_IDS} (info icon).
 *
 * Module-scope English defaults kept for backward compatibility with consumers
 * outside the simulation-results subtree. Translated labels are produced via
 * {@link buildUnmatchedRideColumns} when called from within a React tree.
 */
export const UNMATCHED_RIDE_COLUMNS: UnmatchedRideColumn[] = [
  { id: 'passengers', label: 'Passengers', get: (r) => formatPassengers(r.passengers) },
  { id: 'request_type', label: 'Request type', get: (r) => str(r.request_type) },
  {
    id: 'from_lat_lon',
    label: 'From lat / lon',
    get: (r) => formatLatLonLine(r, 'from_latitude', 'from_longitude'),
    monospace: true,
  },
  { id: 'from_address', label: 'From address', get: (r) => str(r.from_address) },
  {
    id: 'to_lat_lon',
    label: 'To lat / lon',
    get: (r) => formatLatLonLine(r, 'to_latitude', 'to_longitude'),
    monospace: true,
  },
  { id: 'to_address', label: 'To address', get: (r) => str(r.to_address) },
  { id: 'reason', label: 'Reason', get: (r) => str(r.reason) },
  { id: 'ride_id', label: 'Ride ID', get: (r) => str(r.ride_id), monospace: true },
  { id: 'user_id', label: 'User ID', get: (r) => str(r.user_id), monospace: true },
  { id: 'request_id', label: 'Request ID', get: (r) => str(r.request_id), monospace: true },
  { id: 'external_reference_id', label: 'External ref', get: (r) => str(r.external_reference_id), monospace: true },
  { id: 'external_user_reference_id', label: 'User ref', get: (r) => str(r.external_user_reference_id), monospace: true },
  { id: 'batch_id', label: 'Batch ID', get: (r) => str(r.batch_id), monospace: true },
  { id: 'rides_name', label: 'Rides name', get: (r) => str(r.rides_name) },
  { id: 'results_id', label: 'Results ID', get: (r) => str(r.results_id), monospace: true },
  { id: 'created_at', label: 'Created', get: (r) => str(r.created_at) },
  { id: 'from_point_id', label: 'From point', get: (r) => str(r.from_point_id), monospace: true },
  { id: 'to_point_id', label: 'To point', get: (r) => str(r.to_point_id), monospace: true },
];

/** Translated equivalent of {@link UNMATCHED_RIDE_COLUMNS} when a translation function is available. */
export function buildUnmatchedRideColumns(t: TFunction<'simulation'>): UnmatchedRideColumn[] {
  return [
    { id: 'passengers', label: t('results.unmatched.columns.passengers'), get: (r) => formatPassengers(r.passengers) },
    { id: 'request_type', label: t('results.unmatched.columns.requestType'), get: (r) => str(r.request_type) },
    {
      id: 'from_lat_lon',
      label: t('results.unmatched.columns.fromLatLon'),
      get: (r) => formatLatLonLine(r, 'from_latitude', 'from_longitude'),
      monospace: true,
    },
    { id: 'from_address', label: t('results.unmatched.columns.fromAddress'), get: (r) => str(r.from_address) },
    {
      id: 'to_lat_lon',
      label: t('results.unmatched.columns.toLatLon'),
      get: (r) => formatLatLonLine(r, 'to_latitude', 'to_longitude'),
      monospace: true,
    },
    { id: 'to_address', label: t('results.unmatched.columns.toAddress'), get: (r) => str(r.to_address) },
    { id: 'reason', label: t('results.unmatched.columns.reason'), get: (r) => str(r.reason) },
    { id: 'ride_id', label: t('results.unmatched.columns.rideId'), get: (r) => str(r.ride_id), monospace: true },
    { id: 'user_id', label: t('results.unmatched.columns.userId'), get: (r) => str(r.user_id), monospace: true },
    { id: 'request_id', label: t('results.unmatched.columns.requestId'), get: (r) => str(r.request_id), monospace: true },
    { id: 'external_reference_id', label: t('results.unmatched.columns.externalRef'), get: (r) => str(r.external_reference_id), monospace: true },
    { id: 'external_user_reference_id', label: t('results.unmatched.columns.userRef'), get: (r) => str(r.external_user_reference_id), monospace: true },
    { id: 'batch_id', label: t('results.unmatched.columns.batchId'), get: (r) => str(r.batch_id), monospace: true },
    { id: 'rides_name', label: t('results.unmatched.columns.ridesName'), get: (r) => str(r.rides_name) },
    { id: 'results_id', label: t('results.unmatched.columns.resultsId'), get: (r) => str(r.results_id), monospace: true },
    { id: 'created_at', label: t('results.unmatched.columns.created'), get: (r) => str(r.created_at) },
    { id: 'from_point_id', label: t('results.unmatched.columns.fromPoint'), get: (r) => str(r.from_point_id), monospace: true },
    { id: 'to_point_id', label: t('results.unmatched.columns.toPoint'), get: (r) => str(r.to_point_id), monospace: true },
  ];
}

/** Ride/user/batch UUIDs — shown only via the info icon (Operations tooltip + Unmatched table). */
export const UNMATCHED_RIDE_IDENTIFIER_COLUMN_IDS: readonly string[] = [
  'ride_id',
  'user_id',
  'external_reference_id',
  'external_user_reference_id',
  'batch_id',
  'results_id',
] as const;

const UNMATCHED_RIDE_IDENTIFIER_COLUMN_ID_SET = new Set<string>(UNMATCHED_RIDE_IDENTIFIER_COLUMN_IDS);

/** Omitted from summary grid and identifier tooltip (kept on the column list for `formatUnmatchedRideCell`). */
const UNMATCHED_RIDE_HIDDEN_FROM_SUMMARY_IDS = new Set<string>(['rides_name']);

/** English-default derived lists (backward compatibility). */
export const UNMATCHED_RIDE_SUMMARY_COLUMNS = UNMATCHED_RIDE_COLUMNS.filter(
  (c) =>
    !UNMATCHED_RIDE_IDENTIFIER_COLUMN_ID_SET.has(c.id) && !UNMATCHED_RIDE_HIDDEN_FROM_SUMMARY_IDS.has(c.id)
);

export const UNMATCHED_RIDE_IDENTIFIER_COLUMNS = UNMATCHED_RIDE_COLUMNS.filter((c) =>
  UNMATCHED_RIDE_IDENTIFIER_COLUMN_ID_SET.has(c.id)
);

export function buildUnmatchedRideSummaryColumns(t: TFunction<'simulation'>): UnmatchedRideColumn[] {
  return buildUnmatchedRideColumns(t).filter(
    (c) =>
      !UNMATCHED_RIDE_IDENTIFIER_COLUMN_ID_SET.has(c.id) && !UNMATCHED_RIDE_HIDDEN_FROM_SUMMARY_IDS.has(c.id)
  );
}

export function buildUnmatchedRideIdentifierColumns(t: TFunction<'simulation'>): UnmatchedRideColumn[] {
  return buildUnmatchedRideColumns(t).filter((c) =>
    UNMATCHED_RIDE_IDENTIFIER_COLUMN_ID_SET.has(c.id)
  );
}

/** Pre-translation overload: returns English defaults. */
export function formatUnmatchedRideCell(ride: UnmatchedRide, columnId: string): string;
export function formatUnmatchedRideCell(ride: UnmatchedRide, columnId: string, t: TFunction<'simulation'>): string;
export function formatUnmatchedRideCell(
  ride: UnmatchedRide,
  columnId: string,
  t?: TFunction<'simulation'>
): string {
  const cols = t ? buildUnmatchedRideColumns(t) : UNMATCHED_RIDE_COLUMNS;
  const col = cols.find((c) => c.id === columnId);
  if (!col) return '—';
  return col.get(ride) ?? '—';
}

/** Clipboard text for from/to coordinate pairs (numeric segments only). */
function latLonClipboardText(
  ride: UnmatchedRide,
  latField: 'from_latitude' | 'to_latitude',
  lonField: 'from_longitude' | 'to_longitude'
): string | null {
  const lat = formatCoord(ride[latField]);
  const lon = formatCoord(ride[lonField]);
  if (lat == null && lon == null) return null;
  if (lat != null && lon != null) return `${lat}, ${lon}`;
  return lat ?? lon ?? null;
}

/** Summary rows that show a quick-copy control (IDs + lat/lon lines). */
export function unmatchedSummaryColumnSupportsQuickCopy(columnId: string): boolean {
  return (
    columnId === 'from_lat_lon' ||
    columnId === 'to_lat_lon' ||
    columnId.endsWith('_id')
  );
}

export function getUnmatchedRideCopyText(ride: UnmatchedRide, columnId: string): string | null;
export function getUnmatchedRideCopyText(
  ride: UnmatchedRide,
  columnId: string,
  t: TFunction<'simulation'>
): string | null;
export function getUnmatchedRideCopyText(
  ride: UnmatchedRide,
  columnId: string,
  t?: TFunction<'simulation'>
): string | null {
  if (columnId === 'from_lat_lon') {
    return latLonClipboardText(ride, 'from_latitude', 'from_longitude');
  }
  if (columnId === 'to_lat_lon') {
    return latLonClipboardText(ride, 'to_latitude', 'to_longitude');
  }
  const cols = t ? buildUnmatchedRideColumns(t) : UNMATCHED_RIDE_COLUMNS;
  const col = cols.find((c) => c.id === columnId);
  const v = col?.get(ride);
  if (v == null || String(v).trim() === '') return null;
  return v;
}

export type QuickCopyTone = 'onLight' | 'onDark';

/** Small control to copy a single value (used in unmatched table, tooltips, identifier panel). */
export const QuickCopyButton: React.FC<{
  textToCopy: string;
  ariaLabel: string;
  tone: QuickCopyTone;
}> = ({ textToCopy, ariaLabel, tone }) => {
  const { t } = useTranslation('simulation');
  const [copied, setCopied] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  React.useEffect(
    () => () => {
      if (timeoutRef.current !== undefined) clearTimeout(timeoutRef.current);
    },
    []
  );

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      if (timeoutRef.current !== undefined) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const isDark = tone === 'onDark';
  return (
    <Tooltip title={copied ? t('results.unmatched.copied') : t('results.unmatched.copy')} enterDelay={isDark ? 200 : 120} leaveDelay={0}>
      <span>
        <IconButton
          type="button"
          size="small"
          onClick={handleClick}
          aria-label={ariaLabel}
          sx={{
            p: 0.2,
            ml: 0.25,
            flexShrink: 0,
            color: isDark ? '#6b7280' : UNMATCHED_RIDE_TOOLTIP_THEME.label,
            '&:hover': {
              color: isDark ? '#171a1f' : UNMATCHED_RIDE_TOOLTIP_THEME.heading,
              backgroundColor: isDark ? 'rgba(0,0,0,0.06)' : 'rgba(15, 23, 42, 0.06)',
            },
            '&:focus-visible': {
              outline: `2px solid ${isDark ? '#171a1f' : UNMATCHED_RIDE_TOOLTIP_THEME.heading}`,
              outlineOffset: 2,
            },
          }}
        >
          <Icon size={14} name="clipboard" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const MONO_FONT =
  'ui-monospace, "Cascadia Code", "Segoe UI Mono", Menlo, Consolas, monospace';

function columnUsesMonospace(col: UnmatchedRideColumn): boolean {
  return (
    col.monospace === true ||
    col.label.includes('lat') ||
    col.label.includes('lon') ||
    col.label.includes('ID')
  );
}

function unmatchedRideHasAnyIdentifierField(ride: UnmatchedRide): boolean {
  return UNMATCHED_RIDE_IDENTIFIER_COLUMNS.some((col) => col.get(ride) != null);
}

/** Label/value rows for ride + batch identifiers (tooltip title). */
const UnmatchedRideIdentifierTooltipBody: React.FC<{
  ride: UnmatchedRide;
  fontFamily: string;
}> = ({ ride, fontFamily }) => {
  const { t } = useTranslation('simulation');
  const fs = '0.8125rem';
  const labelFs = '0.75rem';

  const identifierRows = buildUnmatchedRideIdentifierColumns(t).map((col) => ({
    col,
    value: col.get(ride),
  })).filter((r): r is { col: UnmatchedRideColumn; value: string } => r.value != null);

  return (
    <Box sx={{ fontFamily, fontSize: fs, maxWidth: 380, py: 0.25 }}>
      {identifierRows.map(({ col, value }, rowIndex) => {
        const mono = columnUsesMonospace(col);
        const stripe =
          rowIndex % 2 === 0
            ? UNMATCHED_RIDE_TOOLTIP_THEME.rowStripeEven
            : UNMATCHED_RIDE_TOOLTIP_THEME.rowStripeOdd;
        return (
          <Box
            key={col.id}
            sx={{
              display: 'flex',
              gap: 0.75,
              alignItems: 'flex-start',
              py: 0.35,
              px: 0.75,
              mx: -0.25,
              borderRadius: 0.5,
              backgroundColor: stripe,
            }}
          >
            <Typography
              component="span"
              sx={{
                color: UNMATCHED_RIDE_TOOLTIP_THEME.label,
                fontWeight: 600,
                fontSize: labelFs,
                minWidth: 92,
                flexShrink: 0,
                letterSpacing: '0.01em',
              }}
            >
              {col.label}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: UNMATCHED_RIDE_TOOLTIP_THEME.value,
                  fontSize: fs,
                  fontWeight: 500,
                  wordBreak: 'break-word',
                  fontFamily: mono ? MONO_FONT : 'inherit',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {value}
              </Typography>
              <QuickCopyButton
                textToCopy={value}
                ariaLabel={t('results.unmatched.copyFieldAria', { label: col.label })}
                tone="onLight"
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/** Opens pickup → dropoff driving directions in Google Maps (new tab). Shown left of the identifiers icon. */
export const UnmatchedRideGoogleMapsButton: React.FC<{
  ride: UnmatchedRide;
  /** Same UTC convention as the Operations timeline heading (e.g. 08:05). */
  requestTimeUtcLabel: string;
}> = ({ ride, requestTimeUtcLabel }) => {
  const { t } = useTranslation('simulation');
  const url = buildUnmatchedRideGoogleMapsDrivingUrl(ride);
  const canOpen = url != null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip
      enterDelay={120}
      leaveDelay={80}
      title={
        canOpen ? (
          <span>
            {t('results.unmatched.directionsTooltip', { time: requestTimeUtcLabel })}
          </span>
        ) : (
          <span>
            {t('results.unmatched.directionsUnavailableTooltip')}
          </span>
        )
      }
    >
      <span>
        <IconButton
          type="button"
          size="small"
          disabled={!canOpen}
          aria-label={
            canOpen
              ? t('results.unmatched.openDirections')
              : t('results.unmatched.directionsUnavailable')
          }
          onClick={handleClick}
          sx={{
            p: 0.25,
            flexShrink: 0,
            color: UNMATCHED_RIDE_TOOLTIP_THEME.label,
            '&:hover': {
              color: UNMATCHED_RIDE_TOOLTIP_THEME.heading,
              backgroundColor: 'rgba(15, 23, 42, 0.06)',
            },
            '&:focus-visible': {
              outline: `2px solid ${UNMATCHED_RIDE_TOOLTIP_THEME.heading}`,
              outlineOffset: 2,
            },
            '&.Mui-disabled': {
              color: 'rgba(15, 23, 42, 0.35)',
            },
          }}
        >
          <Icon size={16} name="car-side" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export interface UnmatchedRideIdsIconProps {
  ride: UnmatchedRide;
  /** Operations timeline tooltip (light) vs Unmatched table row (dark chrome). */
  surface: 'timelineTooltip' | 'tableRow';
  /** Top-right beside time/trip heading in Operations unmatched tooltip (tighter margins). */
  layout?: 'default' | 'tooltipHeader';
}

/**
 * Info icon; hover shows ride/batch identifiers (incl. Ride ID) with a copy control per field.
 */
export const UnmatchedRideIdsIcon: React.FC<UnmatchedRideIdsIconProps> = ({
  ride,
  surface,
  layout = 'default',
}) => {
  const { t } = useTranslation('simulation');
  if (!unmatchedRideHasAnyIdentifierField(ride)) return null;

  const fontFamily = '"Roboto", system-ui, -apple-system, "Segoe UI", sans-serif';
  const isTable = surface === 'tableRow';
  const isHeader = layout === 'tooltipHeader' && surface === 'timelineTooltip';

  return (
    <Tooltip
      title={<UnmatchedRideIdentifierTooltipBody ride={ride} fontFamily={fontFamily} />}
      placement={isTable ? 'left' : 'right'}
      arrow
      enterDelay={120}
      leaveDelay={280}
      disableInteractive={false}
      slotProps={{
        popper: {
          sx: unmatchedRideTooltipPopperSx,
        },
      }}
    >
      <IconButton
        type="button"
        size="small"
        aria-label={t('results.unmatched.showRideIdentifiers')}
        onClick={(e) => {
          e.stopPropagation();
        }}
        sx={{
          p: isHeader ? 0.25 : 0.35,
          ml: isTable ? 0 : isHeader ? 0 : -0.25,
          mt: isTable ? 0 : isHeader ? 0 : 0.25,
          mr: isHeader ? 0.5 : undefined,
          flexShrink: 0,
          alignSelf: isHeader ? 'center' : undefined,
          color: isTable ? '#6b7280' : UNMATCHED_RIDE_TOOLTIP_THEME.label,
          '&:hover': {
            color: isTable ? '#171a1f' : UNMATCHED_RIDE_TOOLTIP_THEME.heading,
            backgroundColor: isTable ? 'rgba(0,0,0,0.04)' : 'rgba(15, 23, 42, 0.06)',
          },
        }}
      >
        <Icon size={20} name="circle-info" style={{ fontSize: isTable ? 18 : isHeader ? 16 : 17 }} />
      </IconButton>
    </Tooltip>
  );
};

export interface UnmatchedRideDetailsProps {
  ride: UnmatchedRide;
  /** Tighter typography for MUI Tooltip; roomier for list cards. */
  variant: 'tooltip' | 'list';
  fontFamily?: string;
  /** When true, omit the info icon (e.g. rendered in Operations tooltip header). */
  hideIdentifiersIcon?: boolean;
}

/**
 * Renders fields from GET /results/{id}/unmatched_rides for UI display.
 * Heavy identifier fields are behind {@link UnmatchedRideIdsIcon}.
 */
const UnmatchedRideDetails: React.FC<UnmatchedRideDetailsProps> = ({
  ride,
  variant,
  fontFamily = '"Roboto", sans-serif',
  hideIdentifiersIcon = false,
}) => {
  const { t } = useTranslation('simulation');
  const isTooltip = variant === 'tooltip';
  const fs = isTooltip ? '0.8125rem' : '0.78rem';
  const labelFs = isTooltip ? '0.75rem' : '0.72rem';

  const rows = buildUnmatchedRideSummaryColumns(t).map((col) => ({ col, value: col.get(ride) }));
  const visibleSummaryRows = rows.filter(
    (r): r is { col: UnmatchedRideColumn; value: string } => r.value != null
  );

  return (
    <Box
      sx={{
        fontFamily,
        fontSize: fs,
        maxWidth: isTooltip ? 340 : '100%',
        // Tooltip scroll is on `.MuiTooltip-tooltip` (see unmatchedRideTooltipPopperSx) so stacked rides share one scrollbar.
        pr: isTooltip ? 0.5 : 0,
      }}
    >
      {visibleSummaryRows.map(({ col, value }, rowIndex) => {
        const copyText = getUnmatchedRideCopyText(ride, col.id, t);
        const showCopy =
          copyText != null && unmatchedSummaryColumnSupportsQuickCopy(col.id);
        const copyTone: QuickCopyTone = isTooltip ? 'onLight' : 'onDark';
        const stripeBg: string | undefined = isTooltip
          ? rowIndex % 2 === 0
            ? UNMATCHED_RIDE_TOOLTIP_THEME.rowStripeEven
            : UNMATCHED_RIDE_TOOLTIP_THEME.rowStripeOdd
          : undefined;
        return (
          <Box
            key={col.id}
            sx={{
              display: 'flex',
              gap: 0.75,
              alignItems: 'flex-start',
              py: 0.35,
              px: isTooltip ? 0.75 : 0,
              mx: isTooltip ? -0.25 : 0,
              borderRadius: isTooltip ? 0.5 : 0,
              backgroundColor: stripeBg,
            }}
          >
            <Typography
              component="span"
              sx={{
                color: isTooltip ? UNMATCHED_RIDE_TOOLTIP_THEME.label : '#374151',
                fontWeight: 600,
                fontSize: labelFs,
                minWidth: isTooltip ? 92 : 100,
                flexShrink: 0,
                letterSpacing: isTooltip ? '0.01em' : undefined,
              }}
            >
              {col.label}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: isTooltip ? UNMATCHED_RIDE_TOOLTIP_THEME.value : '#ffffff',
                  fontSize: fs,
                  fontWeight: isTooltip ? 500 : 400,
                  wordBreak: 'break-word',
                  fontFamily: columnUsesMonospace(col) ? MONO_FONT : 'inherit',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {value}
              </Typography>
              {showCopy ? (
                <QuickCopyButton
                  textToCopy={copyText}
                  ariaLabel={t('results.unmatched.copyFieldAria', { label: col.label })}
                  tone={copyTone}
                />
              ) : null}
            </Box>
          </Box>
        );
      })}
      {!hideIdentifiersIcon &&
        (isTooltip ? (
          <UnmatchedRideIdsIcon ride={ride} surface="timelineTooltip" />
        ) : (
          <UnmatchedRideIdsIcon ride={ride} surface="tableRow" />
        ))}
    </Box>
  );
};

export default UnmatchedRideDetails;
